import React, { useState, useEffect } from 'react';
import { ReactComponent as VerifyAccountIcon } from 'assets/icons/verify-account.svg';
import { Button, Input, ToastMessage } from '@cokitchen/cokitchen-components';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { ROUTE_KEYS, STORAGE_KEYS } from 'utils/constants';
import useDashboardContext from 'contexts/dashboard.context';
import { apiService } from 'services/base.service';
import { handleApiError } from 'utils/functions';

const ChangeEmail = () => {
  const navigate = useNavigate();

  const [timeLeft, setTimeLeft] = useState(0);
  const [resendCount, setResendCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');

  const email = localStorage.getItem(STORAGE_KEYS.EMAIL) || '';

  const { setHideSidebar, setBackLinkTitle, setIsBackLink, setBackLink } = useDashboardContext();

  useEffect(() => {
    setHideSidebar(true);
    setIsBackLink(true);
    setBackLinkTitle('Return to Account');
    setBackLink(ROUTE_KEYS.PROFILE);
    return () => {
      setHideSidebar(false);
      setIsBackLink(false);
      setBackLinkTitle('');
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (timeLeft > 0) {
        setTimeLeft((prev) => prev - 1);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [timeLeft]);

  useEffect(() => {
    resendOtp();
  }, []);

  const resendOtp = async () => {
    setLoading(true);
    setTimeLeft(60 * 2 ** resendCount);
    setResendCount(resendCount + 1);
    try {
      const res = await apiService.post('member/send-otp');
      if (res.status === 201) {
        setOtp('');
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const otpIsValid = (otpValue: string) => {
    setOtpError('');
    const regExp = /^[0-9]{4,4}$/;
    if (regExp.test(otpValue)) {
      return true;
    }
    setOtpError('Invalid OTP');
    return false;
  };

  const verifyOtp = async (otpValue: string) => {
    const data = {
      otp: otpValue
    };
    try {
      const res = await apiService.post('member/verify-otp', { data });
      if (res.status === 201) {
        toast(<ToastMessage type='success' message={res.data.message || 'OTP verified'} />);
        navigate(`/${ROUTE_KEYS.NEW_EMAIL}`, {
          state: {
            from: `/${ROUTE_KEYS.CHANGE_EMAIL}`,
            otpVerified: true
          }
        });
      }
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const otpValue = e.target.value;
    setOtp(otpValue);
    if (otpValue.length === 4 && otpIsValid(otpValue)) {
      verifyOtp(otpValue);
    }
  };

  return (
    <div className='mx-auto max-w-[360px]'>
      <VerifyAccountIcon className='mb-8' />
      <p className='text-[#6F7376] max-mb-[80px]'>
        An OTP has been sent to{' '}
        <span className='text-[#0080E5] text-base font-medium mx-1'>{email}</span> Please check your
        email.
      </p>

      <Input
        design='bordered'
        placeholder='Enter OTP'
        label='Enter OTP'
        type='text'
        value={otp}
        error={otpError}
        onChange={handleOtpChange}
      />
      {timeLeft === 0 ? null : (
        <div className='text-sm'>
          <p className='text-[#3FB827] mt-6'>
            Didn’t receive code?
            <span className='text-[#2E3031] pl-1 text-medium'>Resend in {timeLeft} seconds</span>
          </p>
        </div>
      )}
      <Button
        small
        color='primary'
        className=' mt-[64px] w-[300px]'
        variant='block'
        onClick={resendOtp}
        loading={loading}
        disabled={timeLeft > 0}>
        Resend
      </Button>
    </div>
  );
};

export default ChangeEmail;
